import { motion } from 'motion/react';
import { useLanguage } from '@/contexts/LanguageContext';
import SEO from '../components/utils/SEO';
import { 
  Shield, 
  ArrowRight, 
  ShieldCheck, 
  Globe, 
  Cloud, 
  Building2, 
  Users,
  Layers,
  Activity,
  Cpu,
  RefreshCw,
  DollarSign,
  Network,
  Handshake
} from 'lucide-react';
import SectionHeader from '@/components/ui/SectionHeader';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import NetworkParticles from '@/components/ui/NetworkParticles';
import { marketsData, MarketKey } from '../data/solutions';
import { getVercelImageUrl } from '@/lib/images';

const getValueProps = (t: any) => [
  {
    title: '24×7',
    description: t('Nosso SOC funciona ininterruptamente com técnicos especializados em desempenho e defesa contra os ataques de negação de serviço.', 'Nuestro SOC funciona ininterrumpidamente con técnicos especializados en rendimiento y defensa contra ataques de denegación de servicio.', 'Our SOC runs around the clock with technicians specialized in performance and defense against denial-of-service attacks.'),
    icon: Activity
  },
  {
    title: t('Gerando valor', 'Generando valor', 'Creating value'),
    description: t('Trabalhamos para reduzir custos operacionais, aumentar a eficiência e criar novas receitas para nossos clientes.', 'Trabajamos para reducir los costos operativos, aumentar la eficiencia y crear nuevos ingresos para nuestros clientes.', 'We work to reduce operational costs, increase efficiency and create new revenue streams for our customers.'),
    icon: DollarSign
  },
  {
    title: 'MSSP',
    description: t('Soluções as-a-service a partir das melhores e mais recentes tecnologias globais.', 'Soluciones as-a-service basadas en las mejores y más recientes tecnologías globales.', 'As-a-service solutions built on the best and most recent global technologies.'),
    icon: ShieldCheck
  }
];

const getTargetAudiences = (t: any) => [
  {
    id: 'operadoras-isps',
    title: t('Para operadoras e ISPs', 'Para operadores e ISP', 'For carriers and ISPs'),
    description: t('Proteção, observabilidade e performance para redes que sustentam a conectividade.', 'Protección, observabilidad y rendimiento para redes que sustentan la conectividad.', 'Protection, observability and performance for the networks that sustain connectivity.'),
    image: '/technabler-home-solucao-isps.webp',
    href: '/solucoes/operadoras-isps'
  },
  {
    id: 'operadores-servicos-nuvem',
    title: t('Para operadores de serviços em nuvem', 'Para operadores de servicios en la nube', 'For cloud service operators'),
    description: t('Proteção e analytics para ambientes digitais e infraestruturas distribuídas.', 'Protección y análisis para entornos digitales e infraestructuras distribuidas.', 'Protection and analytics for digital environments and distributed infrastructures.'),
    image: '/technabler-home-solucao-cloud.webp',
    href: '/solucoes/operadores-servicos-nuvem'
  },
  {
    id: 'redes-corporativas',
    title: t('Para redes corporativas', 'Para redes corporativas', 'For corporate networks'),
    description: t('Confiabilidade, segurança e operação especializada para a TI de organizações.', 'Fiabilidad, seguridad y funcionamiento especializado para la TI organizativa.', 'Reliability, security and specialized operation for enterprise IT.'),
    image: '/technabler-home-solucao-rede-corp.webp',
    href: '/solucoes/redes-corporativas'
  },
  {
    id: 'ti-corporativa',
    title: t('Para TI corporativa', 'Para TI corporativa', 'For corporate IT'),
    description: t('Eficiência operacional e visibilidade total para equipes de TI e segurança.', 'Eficiencia operativa y visibilidad total para los equipos de TI y seguridad.', 'Operational efficiency and full visibility for IT and security teams.'),
    image: '/technabler-home-solucao-rede-ti-corp.webp',
    href: '/solucoes/ti-corporativa'
  }
];

const getDifferentiators = (t: any) => [
  {
    title: t('Entrega como serviço', 'Entrega como servicio', 'As-a-service delivery'),
    description: t('Soluções estruturadas para facilitar a adoção, acelerar implantação e priorizar Opex em vez de mobilização pesada de Capex.', 'Soluciones estructuradas para facilitar la adopción, acelerar el despliegue y priorizar OpEx sobre movilizaciones pesadas de CapEx.', 'Solutions structured to ease adoption, speed up deployment and prioritize Opex over heavy Capex commitments.'),
    icon: Layers
  },
  {
    title: t('Operação especializada', 'Operación especializada', 'Specialized operation'),
    description: t('Estrutura de suporte, acompanhamento e serviços gerenciados para ambientes complexos que exigem mais previsibilidade.', 'Estructura de soporte, monitorización y servicios gestionados para entornos complejos que requieren una mayor previsibilidad.', 'Support structure, follow-up and managed services for complex environments that demand more predictability.'),
    icon: Activity
  },
  {
    title: t('Arquitetura ajustada ao contexto', 'Arquitectura adaptada al contexto', 'Architecture tailored to context'),
    description: t('Integração de tecnologias e adaptação da solução conforme o ambiente, a escala e o objetivo operacional do cliente.', 'Integración y adaptación de la solución al entorno, escala y objetivos operativos del cliente.', 'Technology integration and solution adaptation according to the customer\'s environment, scale and operational goals.'),
    icon: Cpu
  },
  {
    title: t('Evolução contínua', 'Evolución continua', 'Continuous evolution'),
    description: t('A solução não termina na implantação. Acompanhamos a operação para garantir adequação, ajustes e evolução ao longo do tempo.', 'La solución no termina en la implantación. Controlamos la operación para garantizar su idoneidad, ajustes y evolución a lo largo del tiempo.', 'The solution does not end at deployment. We follow the operation to ensure fit, adjustments and evolution over time.'),
    icon: RefreshCw
  },
  {
    title: t('White label e monetização', 'Marca blanca y monetización', 'White label and monetization'),
    description: t('Quando o cenário permite, a infraestrutura do cliente também pode se tornar base para novos serviços e diferenciação de mercado.', 'Cuando el entorno lo permite, la infraestructura del cliente también puede convertirse en la base de nuevos servicios y de diferenciación de mercado.', 'When the scenario allows, the customer\'s infrastructure can also become the basis for new services and market differentiation.'),
    icon: DollarSign
  }
];

const getMindTheSecHighlights = (t: any) => [
  {
    title: 'Joint Venture Partner Program',
    desc: t('Parceria para ampliar capacidades e gerar oportunidades conjuntas em observabilidade e segurança.', 'Alianza para ampliar capacidades y generar oportunidades conjuntas en observabilidad y seguridad.', 'Partnership to expand capabilities and generate joint opportunities in observability and security.'),
    icon: Handshake
  },
  {
    title: 'White Label SOC',
    desc: t('Ofereça a seus clientes os serviços de SOC da TechEnabler com sua própria marca.', 'Ofrece a tus clientes los servicios de SOC de TechEnabler con tu propia marca.', "Offer your customers TechEnabler's SOC services under your own brand."),
    icon: ShieldCheck
  }
];

const partners = ['AVSystem', 'Corero', 'Kentik', 'Planisys', 'Unryo'];

const whatsappNumber = '5511976589319';
const whatsappFaleConosco = `https://wa.me/${whatsappNumber}?text=Quero%20falar%20com%20a%20TechEnabler`;
const whatsappDemonstracao = `https://wa.me/${whatsappNumber}?text=Quero%20solicitar%20uma%20demonstra%C3%A7%C3%A3o`;
const whatsappEspecialista = `https://wa.me/${whatsappNumber}?text=Quero%20falar%20com%20um%20especialista`;

// Convite Mind The Sec substitui a seção do Encontro de Usuários até esta data; depois disso o convite some e o Encontro volta.
const mindTheSecCutoff = new Date('2026-09-21T03:00:00-03:00');

export default function Home() {
  const { t } = useLanguage();
  const showMindTheSec = new Date() < mindTheSecCutoff;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <SEO 
        title="TechEnabler"
        description="A TechEnabler integra, opera e evolui soluções para ambientes críticos. Proteja sua infraestrutura com inteligência e performance global."
        canonical="/"
      />
      {/* 1. Hero */}
      <section className="relative min-h-screen pt-32 pb-20 flex items-center overflow-hidden">
        <NetworkParticles />
        <div className="container-custom grid lg:grid-cols-2 gap-12 items-center relative z-10">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="inline-flex items-center gap-3 mb-10 px-4 py-2 bg-primary/10 backdrop-blur-sm border border-primary/20 rounded-full group hover:border-primary/40 transition-all cursor-default max-w-full lg:max-w-fit shadow-sm">
              <div className="relative flex h-1.5 w-1.5 shrink-0">
                <div className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></div>
                <div className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary"></div>
              </div>
              <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-primary leading-none">
                {t('Observabilidade, segurança e desempenho', 'Observabilidad, seguridad y rendimiento', 'Observability, security and performance')}
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl xl:text-7xl font-bold text-foreground mb-8 leading-[1.05] tracking-tight pr-10">
              {t('Garanta o desempenho de sua rede', 'Garantice el rendimiento de su red', 'Ensure your network performance')}
            </h1>
            <p className="text-lg text-muted-foreground mb-12 leading-relaxed max-w-xl">
              {t('Somos uma empresa brasileira integradora de tecnologias, com foco na observabilidade de redes, segurança e gerenciamento de dispositivos, tanto para operadoras de serviços de rede quanto para o mercado empresarial.', 'Somos una empresa brasileña que integra tecnologías centrándose en la observabilidad de la red, la seguridad y la gestión de dispositivos tanto de red como corporativos.', 'We are a Brazilian technology integrator focused on network observability, security and device management, serving both network service providers and the enterprise market.')}
            </p>
            <div className="flex flex-wrap gap-6 items-center">
              <Button asChild className="px-8 py-6 text-base">
                <a href={whatsappFaleConosco} target="_blank" rel="noopener noreferrer">
                  {t('Fale com a TechEnabler', 'Hablar con TechEnabler', 'Talk to TechEnabler')}
                </a>
              </Button>
              <Button variant="outline" asChild className="px-8 py-6 text-base">
                <a href={whatsappDemonstracao} target="_blank" rel="noopener noreferrer">
                  {t('Solicite uma demonstração', 'Solicitar una demostración', 'Request a demo')}
                </a>
              </Button>
              <Link to="/solucoes" className="text-sm font-bold text-foreground hover:text-primary transition-colors flex items-center gap-2 group">
                {t('Conheça nossas soluções', 'Conoce nuestras soluciones', 'Explore our solutions')} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>

          {/* Visual Composition */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="relative hidden lg:block"
          >
            <div className="aspect-square relative flex items-center justify-center">
              <div className="absolute inset-0 bg-primary/20 rounded-full blur-3xl animate-pulse" />
              <div className="relative w-full h-full border border-border rounded-2xl overflow-hidden shadow-2xl bg-secondary/50">
                <img src="/technabler-home-hero-01.webp" alt="Proteção da Infraestrutura" className="w-full h-full object-cover" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. Video Evento — volta a aparecer a partir de 21/09/2026 03h, quando o convite do Mind The Sec é removido */}
      {!showMindTheSec && (
      <section className="py-24 bg-card/10 border-b border-border">
        <div className="container-custom">
          <SectionHeader
            centered
            subtitle={t("Evento", "Evento", "Event")}
            title={t("1º Encontro de Usuários TechEnabler", "I Encuentro de Usuarios TechEnabler", "1st TechEnabler User Meeting")}
            description={t("Realizado em maio de 2026, em São Paulo, o Primeiro Encontro de Usuários TechEnabler reuniu clientes e parceiros de negócio, incluindo alguns dos maiores provedores de Internet do País e empresas globais de tecnologia. Além de celebrar os cinco anos da empresa e a confiança do mercado brasileiro, que impulsionou sua expansão na América Latina, o encontro marcou um momento de escuta e troca de ideias sobre os próximos desafios em segurança e otimização de redes.", "Realizado en mayo de 2026, en São Paulo, el Primer Encuentro de Usuarios TechEnabler reunió a clientes y socios de negocio, incluyendo algunos de los mayores proveedores de Internet del país y empresas globales de tecnología. Además de celebrar los cinco años de la empresa y la confianza del mercado brasileño, que impulsó su expansión en América Latina, el encuentro marcó un momento de escucha e intercambio de ideas sobre los próximos desafíos en seguridad y optimización de redes.", "Held in May 2026 in São Paulo, the First TechEnabler User Meeting brought together customers and business partners, including some of the country's largest Internet providers and global technology companies. In addition to celebrating the company's five years and the trust of the Brazilian market, which drove its expansion into Latin America, the meeting marked a moment to listen and exchange ideas about the next challenges in network security and optimization.")}
          />
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-16 max-w-5xl mx-auto rounded-[2.5rem] overflow-hidden border border-border shadow-2xl shadow-black/10 aspect-video relative group bg-black"
          >
            <video
              className="w-full h-full object-cover"
              src="https://pocchdybgijyiasysgka.supabase.co/storage/v1/object/public/techenabler-assets/videos/encontro-usuarios-techenabler.mp4"
              title="1º Encontro de Usuários TechEnabler Highlights"
              autoPlay
              muted
              loop
              controls
              playsInline
              preload="metadata"
            />
          </motion.div>
        </div>
      </section>
      )}

      {/* 2.5 Convite Mind The Sec — com gif — some a partir de 21/09/2026 03h, quando o Encontro de Usuários acima volta a aparecer */}
      {showMindTheSec && (
      <section className="py-24 border-b border-border overflow-hidden">
        <div className="container-custom grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="kicker text-primary">{t('Próximo evento', 'Próximo evento', 'Upcoming event')}</span>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight tracking-tight text-foreground">
              {t('Esperamos você no Mind The Sec', 'Te esperamos en el Mind The Sec', "We'll be waiting for you at Mind The Sec")}
            </h2>
            <p className="text-sm leading-relaxed text-muted-foreground mb-8">
              {t('É a sua oportunidade de colocar na mesa os desafios que pesam na segurança da sua rede e no desempenho das suas operações. Venha conversar com quem pode apresentar uma solução.', 'Es tu oportunidad de poner sobre la mesa los desafíos que pesan en la seguridad de tu red y en el desempeño de tus operaciones. Ven a conversar con quien puede presentarte una solución.', "It's your chance to put on the table the challenges weighing on your network's security and your operations' performance. Come talk to someone who can present a solution.")}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-foreground mb-6">
              {t('A TechEnabler também traz novidades para incrementar seus negócios:', 'TechEnabler también trae novedades para impulsar tu negocio:', 'TechEnabler is also bringing news to boost your business:')}
            </p>
            <div className="space-y-4 mb-8">
              {getMindTheSecHighlights(t).map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  className="flex gap-4 p-4 rounded-xl border border-border hover:border-primary/30 hover:bg-secondary/30 transition-colors"
                >
                  <div className="shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <item.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-foreground mb-1">{item.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            <p className="text-sm font-bold text-primary">
              {t('Vale a visita. Não fique de fora.', 'Vale la visita. No te quedes fuera.', "It's worth the visit. Don't miss it.")}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-primary/15 rounded-full blur-3xl" />
            <div className="relative rounded-2xl overflow-hidden border border-border shadow-2xl shadow-black/10 bg-secondary/50 flex items-center justify-center">
              <img
                src="https://pocchdybgijyiasysgka.supabase.co/storage/v1/object/public/techenabler-assets/images/mind-the-sec-2026.gif"
                alt="TechEnabler no Mind The Sec 2026"
                className="w-full h-auto"
              />
            </div>
          </motion.div>
        </div>
      </section>
      )}

      {/* 3. Proposta de Valor */}
      <section className="py-24 bg-secondary border-y border-border relative overflow-hidden">
        <NetworkParticles particleCount={30} />
        <div className="container-custom relative z-10">
          <SectionHeader
            variant="default"
            centered
            subtitle={t("Serviços", "Servicios", "Services")}
            title={t("Serviços e modelos de negócio", "Servicios y modelos de negocio", "Services and business models")}
            description={
              <div className="space-y-4">
                <p>
                  {t('Como MSSP, nossa abordagem consiste em modelar as principais tecnologias do mercado global de modo personalizado para o cliente. Temos opções via Capex ou Opex, com pagamento pela capacidade utilizada, o que permite atender desde operações de pequeno porte até as de alcance internacional.', 'Como MSSP, nuestro enfoque es modelar tecnologías base al cliente. Ofrecemos opciones OpEx o CapEx, permitiendo soportar múltiples tamaños de clientes a nivel mundial.', 'As an MSSP, our approach is to shape the leading technologies of the global market around each customer. We offer Capex or Opex options, with payment based on the capacity used, which allows us to serve everything from small operations to international ones.')}
                </p>
                <p>
                  {t('E oferecemos a venda white label de nossos serviços, o que proporciona ainda mais valor ao cliente.', 'Ofrecemos marca blanca, añadiendo aún más valor a nuestro cliente.', 'We also offer white-label resale of our services, delivering even more value to the customer.')}
                </p>
              </div>
            }
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {getValueProps(t).map((prop, index) => (
              <motion.div
                key={prop.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-background/80 backdrop-blur-sm text-foreground border border-border rounded-xl p-8 transition-all hover:border-primary/30 hover:bg-background shadow-sm"
              >
                <prop.icon className="h-6 w-6 text-primary mb-6" />
                <h3 className="text-sm font-bold text-foreground mb-4">{prop.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {prop.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Para quem é */}
      <section className="py-24">
        <div className="container-custom">
          <SectionHeader
            title={t("Soluções estruturadas para cada tipo de operação", "Soluciones estructuradas para cada tipo de operación", "Solutions structured for each type of operation")}
            description={t("A TechEnabler organiza sua oferta conforme o perfil operacional do cliente. Em vez de apresentar um portfólio genérico, estrutura soluções por caso de uso, o que torna a navegação mais clara e a proposta de valor mais alinhada a cada operação.", "TechEnabler organiza su oferta según el perfil del cliente, adaptando en base al uso que agiliza y adecúa su entrega.", "TechEnabler organizes its offering around each customer's operational profile. Instead of presenting a generic portfolio, it structures solutions by use case, making navigation clearer and the value proposition better aligned with each operation.")}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {getTargetAudiences(t).map((audience, index) => (
              <div 
                key={audience.id}
                className="group"
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card-sleek p-0 h-full flex flex-col justify-between hover:border-primary/50 transition-colors overflow-hidden"
                >
                  <div className="relative h-48 overflow-hidden bg-primary/5 group-hover:bg-primary/10 transition-colors flex items-center justify-center">
                    <img src={audience.image} alt={audience.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-10 pt-4 flex-1 flex flex-col">
                    <h3 className="text-xl font-bold text-foreground mb-4">{audience.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                      {audience.description}
                    </p>
                    
                    <div className="mt-auto pt-6 border-t border-border/50">
                      <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-3">{t('Selecione o alcance:', 'Seleccione el alcance:', 'Select the scope:')}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-2">
                        {[{id: 'Local', l: t('Local', 'Local', 'Local')}, {id: 'Regional', l: t('Regional', 'Regional', 'Regional')}, {id: 'Global', l: t('Global', 'Global', 'Global')}].map((scope) => (
                          <Link 
                            key={scope.id}
                            to={`/solucoes/${audience.id}/${scope.id.toLowerCase()}`}
                            className="text-[11px] font-bold uppercase tracking-widest text-blue-600 hover:text-blue-800 transition-colors"
                          >{scope.l}</Link>
                        ))}
                      </div>
                    </div>

                    <Link to={audience.href} className="mt-6 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-primary group-hover:text-primary/60 transition-colors">
                      {t('Veja os detalhes', 'Ver detalles', 'See details')} <ArrowRight className="h-3 w-3" />
                    </Link>
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Modelo de entrega e diferenciais */}
      <section className="py-24">
        <div className="container-custom grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <SectionHeader
              subtitle={t("Diferenciais", "Diferenciales", "Differentiators")}
              title={t("Mais que tecnologia: serviço, operação e evolução contínua", "Más que tecnología: servicio y la evolución continua", "More than technology: service, operation and continuous evolution")}
              description={t("A TechEnabler não entrega apenas ferramentas. A proposta combina tecnologia, integração, operação e acompanhamento contínuo para transformar solução técnica em resultado real de negócio. Isso inclui modelos as-a-service, suporte especializado, personalização, analytics, estruturas white label e formatos ajustados à realidade de cada cliente.", "TechEnabler no solo ofrece herramientas, pero agrupa las soluciones, as-a-service en la búsqueda por crecimiento.", "TechEnabler does not just deliver tools. The proposal combines technology, integration, operation and continuous follow-up to turn technical solutions into real business results. This includes as-a-service models, specialized support, customization, analytics, white-label structures and formats adjusted to each customer's reality.")}
            />
            <div className="mt-12 relative aspect-video rounded-2xl overflow-hidden border border-border group bg-secondary/30">
              <img src="/technabler-home-diferenciais-01.webp" alt="Monitoramento Ativo e Diferenciais" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute bottom-6 left-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/20 backdrop-blur-sm flex items-center justify-center">
                  <Activity className="h-5 w-5 text-primary" />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-foreground">{t('Monitoramento ativo 24/7', 'Monitoreo activo 24/7', 'Active 24/7 monitoring')}</span>
              </div>
            </div>
          </div>
          <div className="space-y-6">
            {getDifferentiators(t).map((diff, index) => (
              <motion.div
                key={diff.title}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 border border-border rounded-xl flex gap-6 group hover:bg-secondary/50 transition-colors"
              >
                <div className="shrink-0 w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                  <diff.icon className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-foreground mb-2 tracking-tight">{diff.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {diff.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Parceiros de tecnologia */}
      <section className="py-24 bg-secondary border-y border-border relative overflow-hidden">
        <NetworkParticles particleCount={25} />
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mb-20">
            <SectionHeader
              variant="default"
              subtitle={t("Ecossistema", "Ecosistema", "Ecosystem")}
              title={t("Tecnologias globais integradas a soluções personalizadas", "Tecnologías globales conectadas a su propia empresa", "Global technologies integrated into customized solutions")}
              description={t("Os parceiros de tecnologia da TechEnabler são reconhecidos no mercado global por sua excelência no que toca a proteção e observabilidade de redes. A TechEnabler conta com essas tecnologias para desenvolver soluções próprias, customizáveis para cada cliente, e apoiadas pelo seu SOC em regime 24x7.", "Los socios tecnológicos de TechEnabler son reconocidos en el mercado global por su excelencia en protección y observabilidad de redes. TechEnabler se apoya en estas tecnologías para desarrollar soluciones propias, personalizables para cada cliente, y respaldadas por su SOC en régimen 24x7.", "TechEnabler's technology partners are recognized in the global market for their excellence in network protection and observability. TechEnabler relies on these technologies to develop its own solutions, customizable for each client, and backed by its SOC operating 24x7.")}
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-8">
            {partners.map(p => (
              <div 
                key={p} 
                className="h-24 md:h-32 flex items-center justify-center p-8 bg-background border border-border rounded-2xl hover:border-primary/40 transition-all group cursor-default shadow-sm hover:shadow-md"
              >
                <span className="text-xl md:text-2xl font-black tracking-tighter text-muted-foreground/90 group-hover:text-primary transition-all duration-500">
                  {p}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-16 flex justify-center">
            <Link 
              to="/parceiros" 
              className="px-8 py-3 rounded-full border border-border text-[10px] font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground hover:bg-background transition-all flex items-center gap-3 shadow-sm"
            >
              {t('Conheça em detalhe nossos parceiros', 'Descubre en detalle nuestros partners', 'Get to know our partners in detail')} <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </section>

      {/* 8. Nossa presença */}
      <section className="py-24 border-t border-border">
        <div className="container-custom">
          <SectionHeader
            centered
            subtitle={t("Escala regional", "Escala regional", "Regional scale")}
            title={t("Nossa presença", "Nuestra presencia", "Our presence")}
            description={t("Já reconhecida no mercado brasileiro, onde conta com mais de 80 clientes, a TechEnabler expande a sua presença na região latino-americana. Oferecemos hoje nossos serviços em oito países, além do Brasil. Atuamos com escritório próprio no México e por meio de parcerias estratégicas na Argentina, Colômbia, Equador, Guatemala, Panamá, Peru e Uruguai.", "Nuestra presencia se destaca en el lado regional, ya que ofrecemos servicio en toda LATAM expandiendo fronteras del sector digital del mercado.", "Already established in the Brazilian market, where it serves more than 80 customers, TechEnabler is expanding its presence across Latin America. Today we offer our services in eight countries beyond Brazil, with our own office in Mexico and strategic partnerships in Argentina, Colombia, Ecuador, Guatemala, Panama, Peru and Uruguay.")}
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20 mt-16">
            <div className="text-center p-12 card-premium bg-secondary/30 border-none relative overflow-hidden group">
              <div className="relative z-10">
                <div className="text-6xl font-bold text-primary mb-4">+80</div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t('Clientes ativos', 'Clientes activos', 'Active customers')}</div>
              </div>
            </div>
            <div className="text-center p-12 card-premium bg-secondary/30 border-none relative overflow-hidden group">
              <div className="relative z-10">
                <div className="text-6xl font-bold text-primary mb-4">20M</div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t('Usuários protegidos', 'Usuarios protegidos', 'Protected users')}</div>
              </div>
            </div>
            <div className="text-center p-12 card-premium bg-secondary/30 border-none relative overflow-hidden group">
              <div className="relative z-10">
                <div className="text-6xl font-bold text-primary mb-4">LATAM</div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t('Expansão regional', 'Expansión regional', 'Regional expansion')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 9. Sala de Imprensa */}
      <section className="py-24 bg-secondary/30">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
            <SectionHeader
              subtitle={t("Sala de Imprensa", "Sala de Prensa", "Newsroom")}
              title={t("Informações sobre a TechEnabler", "Investigaciones de TechEnabler", "Information about TechEnabler")}
              description={t("Acompanhe o que comunicamos ao mercado, nossa presença em eventos e o que contam nossos clientes.", "Nuestras noticias, las que mandamos junto la presencia.", "Follow what we communicate to the market, our presence at events and what our customers have to say.")}
              className="mb-0"
            />
            <Button variant="outline" asChild className="shrink-0 bg-transparent border-primary/20 text-primary hover:bg-primary/5">
              <Link to="/sala-de-imprensa">
                {t('Ver Sala de Imprensa', 'Ver Sala de Prensa', 'Visit the Newsroom')}
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: t('Press releases', 'Comunicados de Prensa', 'Press releases'), desc: t('O que comunicamos ao mercado', 'Comunicados oficiales', 'What we communicate to the market') },
              { title: t('Mídia', 'Medios', 'Media'), desc: t('Informações e imagem da TechEnabler', 'Noticias y Medios de las cosas', 'TechEnabler information and brand assets') },
              { title: t('Estudos de caso', 'Casos de Estudio', 'Case studies'), desc: t('O que contam nossos clientes', 'Las referencias locales', 'What our customers have to say') },
              { title: t('Eventos', 'Eventos', 'Events'), desc: t('Presença nos maiores eventos de tecnologia', 'De eventos presenciales', 'Presence at the biggest technology events') }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-8 bg-card border border-border rounded-2xl shadow-sm hover:shadow-md transition-all group"
              >
                <h3 className="text-lg font-bold text-foreground mb-4 group-hover:text-primary transition-colors">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-6">{item.desc}</p>
                <Link to="/sala-de-imprensa" className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-primary group-hover:text-primary/60 transition-colors">
                  {t('Acesse aqui', 'Acceder Aquí', 'Access here')} <ArrowRight className="h-3 w-3" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 10. CTA Final */}
      <section className="py-32 bg-background border-t border-border relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2" />
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-6xl font-bold text-foreground mb-8 leading-tight tracking-tight">
              {t('Vamos desenhar a solução certa para o seu ambiente', 'Vamos configurar lo correcto en su area propia', 'Let\'s design the right solution for your environment')}
            </h2>
            <p className="text-xl text-muted-foreground mb-12 leading-relaxed">
              {t('Se sua operação exige mais proteção, visibilidade e capacidade de evolução, a TechEnabler pode ajudar a estruturar a combinação certa de soluções para você. Fale com nossa equipe e entenda como transformar sua infraestrutura em uma operação mais eficiente, previsível e preparada para crescer.', 'Pruébelo ahora para dar su salto evolutivo en todo.', 'If your operation demands more protection, visibility and room to evolve, TechEnabler can help structure the right combination of solutions for you. Talk to our team and learn how to turn your infrastructure into a more efficient, predictable operation, ready to grow.')}
            </p>
            <div className="flex flex-wrap justify-center gap-6 items-center mb-12">
              <Button asChild className="px-10 py-7 text-base">
                <a href={whatsappEspecialista} target="_blank" rel="noopener noreferrer">
                  {t('Fale com um especialista', 'Consulte un especialista', 'Talk to a specialist')}
                </a>
              </Button>
              <Button variant="outline" asChild className="px-10 py-7 text-base">
                <a href={whatsappDemonstracao} target="_blank" rel="noopener noreferrer">
                  {t('Solicite uma demonstração', 'Solicitar una demostración', 'Request a demo')}
                </a>
              </Button>
              <Button variant="outline" asChild className="px-10 py-7 text-base">
                <Link to="/solucoes">
                  {t('Explore as soluções', 'Explore soluciones', 'Explore the solutions')}
                </Link>
              </Button>
            </div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
              {t('Conversa orientada ao seu perfil, à sua operação e às prioridades do seu negócio.', 'Consultas orientadas al cliente.', 'A conversation oriented to your profile, your operation and your business priorities.')}
            </p>
          </div>
        </div>
      </section>
    </motion.div>
  );
}

